import { html, TemplateResult } from 'lit';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';
import { LangQuoteHelperFunctions } from '../../../../localization/scripts/quotes/quote-helper-functions-lang';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import { stagedExecution } from '../../../../webmodule-common/other/general/event-source-client';
import { fireQuickErrorToast, fireQuickSuccessToast } from '../../../../webmodule-common/other/toast-away';
import { buttonsOkCancel } from '../../../../webmodule-common/other/ui/modal-confirmation';
import { createCancelButton } from '../../../../webmodule-common/other/ui/modal-footer-buttons';
import { LitBaseModal } from '../../../../webmodule-common/other/ui/modal/modal-factory-lit';
import { containsFlag, firstValidString } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { QuoteState } from '../../../api/dealer-api-interface-quote';
import { WMEventSource } from '../../../api/event-source';
import { SSEQuote } from '../../../quotes/data/sse-quote';
import { launchQuote } from '../../../quotes/ui/launcher';

interface QuoteCopyCompletionMonitorData {
  quoteId: string;
  dialog: QuoteCopyCompletionDialog;
  waitForCompletion: Promise<void>;
}
let activeMonitorList: QuoteCopyCompletionMonitorData[] = [];
const signalledQuotes: Set<string> = new Set();
/**
 * Monitors SSE quote events for copy completion and displays a success toast
 * notification allowing the user to quickly navigate to the newly copied quote.
 *
 * Registered globally at application startup to monitor all quote copy operations
 * across the application, not just within the quote list view.
 */
export function registerQuoteCopyCompletionMonitor(): void {
  const quoteCopyMonitor = stagedExecution({
    threshold: 100, // Low threshold for immediate user feedback
    event: async () => {
      // No batch action needed - we act immediately per event
    },
    eventActioned: async (data: SSEQuote) => {
      // Only show toast if:
      // 1. The quote belongs to the current user (they initiated the copy)
      // 2. The quote was previously in Copying state
      // 3. The quote is no longer in Copying state (copy completed)
      if (
        data.creationUserId === getCurrentUser()?.id &&
        containsFlag(data.previousState ?? QuoteState.None, QuoteState.Copying) &&
        !containsFlag(data.state, QuoteState.Copying)
      ) {
        signalledQuotes.add(data.id);
        const activeMonitor = activeMonitorList.find(monitor => monitor.quoteId === data.id);
        activeMonitorList = activeMonitorList.filter(monitor => monitor.quoteId !== data.id);

        // Check if copy failed (quote was deleted)
        if (containsFlag(data.state, QuoteState.Deleted)) {
          const errorMessage = data.message;
          const errorContent = html`
            <div class="quote-copy-failed-toast">
              <span
                >${LangFranchiseeQuoteListView.quoteCopyFailed(
                  firstValidString(data.quoteNumber, LangQuoteHelperFunctions.draft()),
                  data.quoteTitle,
                  errorMessage
                )}</span
              >
            </div>
          `;
          fireQuickErrorToast(errorContent, 10000);
        } else {
          // Copy succeeded - show success toast
          const toastContent = html`
            <div class="quote-copied-toast">
              <span
                >${LangFranchiseeQuoteListView.quoteCopiedSuccessfully(
                  firstValidString(data.quoteNumber, LangQuoteHelperFunctions.draft()),
                  data.quoteTitle
                )}</span
              >
              <button data-bs-dismiss="toast" @click=${() => launchQuote(data.id)} class="launch">
                ${LangFranchiseeQuoteListView.viewQuote()}
              </button>
            </div>
          `;

          if (!activeMonitor) {
            //there was never a montior dialog shown or it was closed, just fire the toast
            fireQuickSuccessToast(toastContent, 30000);
          } else {
            activeMonitor.dialog.quoteCompleted = true;
          }
        }
      }
    }
  });

  WMEventSource.getInstance().addEventListener(WMEventSource.quote, quoteCopyMonitor);
}

export async function monitorQuoteCopyCompletion(quoteId: string) {
  const activeMonitor = activeMonitorList.find(monitor => monitor.quoteId === quoteId);
  if (activeMonitor) {
    // Already being monitored
    return activeMonitor.waitForCompletion;
  }

  const dialog = new QuoteCopyCompletionDialog();
  dialog.quoteId = quoteId;
  dialog.quoteCompleted = signalledQuotes.has(quoteId);
  const monitorData = {
    quoteId,
    dialog: dialog,
    waitForCompletion: buildMonitorPromise()
  };
  activeMonitorList.push(monitorData);

  return monitorData.waitForCompletion;
  async function buildMonitorPromise(): Promise<void> {
    const waiter = await dialog.show();
    await waiter.onClose;
    //this could be first or last, but if we remove here, the trigger cant trigger later.
    activeMonitorList = activeMonitorList.filter(monitor => monitor.quoteId !== quoteId);
  }
}
class QuoteCopyCompletionDialog extends LitBaseModal {
  private _quoteCompleted: boolean;
  public get quoteCompleted(): boolean {
    return this._quoteCompleted;
  }
  public set quoteCompleted(value: boolean) {
    this._quoteCompleted = value;
    this.requestUpdate();
  }
  private _quoteId: string | null = null;
  public get quoteId(): string | null {
    return this._quoteId;
  }
  public set quoteId(value: string | null) {
    this._quoteId = value;
    this.requestUpdate();
  }
  title(): string | TemplateResult {
    return this.quoteCompleted
      ? LangQuoteHelperFunctions.quoteCopyComplete()
      : LangQuoteHelperFunctions.processingPleaseWait();
  }
  bodyTemplate(): TemplateResult {
    if (this.quoteCompleted) {
      return html`
        <div class="d-flex justify-content-center mb-3">
          <i class="fa fa-check-circle text-success" style="font-size: 3rem;"></i>
        </div>
        <div class="text-center">${LangQuoteHelperFunctions.waitingForQuoteCopyToComplete(this._quoteCompleted)}</div>
      `;
    }
    return html`
      <div class="d-flex justify-content-center mb-3">
        <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
      <div class="text-center">${LangQuoteHelperFunctions.waitingForQuoteCopyToComplete(this._quoteCompleted)}</div>
    `;
  }

  footerTemplate(): TemplateResult | null | undefined {
    if (this.quoteCompleted) {
      return this.createConfirmCancelButtons(
        LangQuoteHelperFunctions.openQuote(),
        () => {
          if (this.quoteId) {
            launchQuote(this.quoteId);
          }
          this.hideModal();
        },
        buttonsOkCancel().cancel
      );
    }
    return createCancelButton(() => {
      this.hideModal();
    }, LangQuoteHelperFunctions.dontWait());
  }
}
